import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin( ScrollTrigger, SplitText );

import Lenis from 'lenis';

let lenis = null;
let lenisInitialized = false;
let scrollTimeout = null;

/**
 * 初始化 Lenis 平滑滾動
 * 使用延遲初始化以減少主線程阻塞時間 (TBT)
 */
export const initLenis = () => {
    if ( lenisInitialized || !import.meta.client ) return;
    lenisInitialized = true;

    lenis = new Lenis( {
        duration: 2,
        easing: ( t ) => Math.min( 1, 1.001 - Math.pow( 3, -10 * t ) ),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 1,
        infinite: false,
    } );

    lenis.on( 'scroll', () => {
        // 更新 ScrollTrigger
        if ( ScrollTrigger ) {
            ScrollTrigger.update();
        }

        // 清除之前的計時器
        clearTimeout( scrollTimeout );

        // 慣性滾動結束後暫停 Lenis（節省資源）
        scrollTimeout = setTimeout( () => {
            if ( lenis && Math.abs( lenis.velocity ) < 0.01 ) {
                lenis.stop();
            }
        }, 100 );
    } );

    gsap.ticker.add( ( time ) => {
        lenis.raf( time * 1000 );
    } );

    // 監聽滾動開始事件，恢復 Lenis
    const startScroll = () => {
        if ( lenis && lenis.isStopped ) {
            lenis.start();
        }
    };

    window.addEventListener( 'wheel', startScroll, { passive: true } );
    window.addEventListener( 'touchstart', startScroll, { passive: true } );

    // 頁面不可見時暫停 Lenis
    document.addEventListener( 'visibilitychange', () => {
        if ( document.hidden && lenis ) {
            lenis.stop();
        }
    } );

    // 初始狀態暫停，等待用戶首次滾動
    lenis.stop();
};

if ( import.meta.client ) {
    // 配置 gsap ticker（不依賴 Lenis，可以立即執行）
    gsap.ticker.lagSmoothing( 0 );
    // gsap.ticker.fps( 60 );

    ScrollTrigger.config( {
        limitCallbacks: true,
        autoRefreshEvents: "DOMContentLoaded,load,resize",
        syncInterval: 2,
        ignoreMobileResize: true,
    } );

    // // 使用 requestIdleCallback 延遲 Lenis 初始化
    // // 這樣可以讓瀏覽器在空閒時再執行，減少主線程阻塞
    if ( 'requestIdleCallback' in window ) {
        requestIdleCallback( initLenis, { timeout: 5000 } );
    } else {
        // Fallback for Safari and older browsers
        initLenis();
    }
}

export const scrollToTop = () => {
    if ( lenis ) {
        // 確保 Lenis 正在運行
        if ( lenis.isStopped ) {
            lenis.start();
        }
        lenis.scrollTo( 0, { duration: 0.5 } );
    } else {
        // Fallback: 如果 Lenis 尚未初始化，使用原生滾動
        window.scrollTo( { top: 0, behavior: 'smooth' } );
    }
};

export const killAnimation = ( tl ) => {
    if ( tl ) {
        if ( Array.isArray( tl ) ) {
            tl.forEach( ( t ) => t.kill() );
        } else {
            tl.kill();
        }
        tl = null;
    }
};

export const textSplitAnimate = ( loadingState, ref, delay = 1.5 ) => {
    if ( !loadingState ) {
        ref.value?.animate( {
            duration: 0.8,
            stagger: 0.05,
            delay: delay,
        } );
    }
};

export const killScrollTrigger = () => {
    ScrollTrigger.killAll();
};

// Re-export gsap and plugins for convenience
export { gsap, ScrollTrigger, SplitText };
